import {AsyncPipe, NgClass, NgFor, NgIf, NgStyle, NgTemplateOutlet} from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import {MatIconModule} from '@angular/material/icon';
import {MatMenuModule, MatMenuTrigger} from '@angular/material/menu';
import {MatTooltipModule} from '@angular/material/tooltip';
import {DomSanitizer, SafeStyle} from '@angular/platform-browser';
import {TranslateModule} from '@ngx-translate/core';
import {DragulaModule, DragulaService, Group} from 'ng2-dragula';
import {Observable, Subject} from 'rxjs';
import {map, takeUntil, tap} from 'rxjs/operators';
import {Q9HtmlDecoderPipe, SnackBarService} from '@q9elements/ui-kit/common';
import {DragulaEvent, DRAGULA_BAG_PREFIXES} from 'src/app/shared/models/dragula.interface';
import {DragulaConfigurationService} from 'src/app/shared/service/dragula-configuration.service';
import {ModeDataService} from '../../editor-state/mode-data.service';
import {DynamicCSSPropertiesDirective} from '../helpers/dynamic-css-properties.directive';
import {FlowlineHeaderHelper} from '../shape-spawn-data/flowline-header.helper';
import {StylesService} from '../styles/styles.service';
import {FL_END_MAPPER, FL_START_MAPPER} from './header.model';
import {IHeaderData, ILegendItem, LegendItemType, SfaHeaderService} from './sfa-header.service';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';

interface ItemMenuOptions {
  edit: OPTION_DISPLAY_TYPES;
  remove: OPTION_DISPLAY_TYPES;
  uploadLogo: OPTION_DISPLAY_TYPES;
  changeLogo: OPTION_DISPLAY_TYPES;
  removeLogo: OPTION_DISPLAY_TYPES;
  changeBackground: OPTION_DISPLAY_TYPES;
}

enum ITEMS_TYPES {
  INFO = 'info',
  LOGO = 'logo'
}

enum OPTION_DISPLAY_TYPES {
  SHOW,
  DISABLE,
  HIDE
}

@Component({
  selector: 'app-sfa-header',
  templateUrl: './sfa-header.component.html',
  styleUrls: ['./sfa-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgIf,
    NgStyle,
    NgTemplateOutlet,
    NgClass,
    DragulaModule,
    NgFor,
    MatTooltipModule,
    MatIconModule,
    DynamicCSSPropertiesDirective,
    MatMenuModule,
    AsyncPipe,
    TranslateModule,
    Q9HtmlDecoderPipe
  ]
})
export class SfaHeaderComponent implements OnInit, OnDestroy {
  public readonly defaultColor = '#e5e5e5';

  isViewMode$ = this.modeDataSvc.isViewMode$;
  headerData$ = this.sfaHeaderSvc.headerData$.pipe(map(header => this.mapLegendItems(header)));
  isArchitecture = this.diagramTypeConfigSvc.isArchitecture;

  FL_START_MAPPER = FL_START_MAPPER;
  FL_END_MAPPER = FL_END_MAPPER;

  menuCoords: {x: string; y: string} = {x: '0', y: '0'};

  @ViewChild('resContainerRef', {read: ElementRef, static: true})
  public resContainerRef: ElementRef<HTMLDivElement>;
  @ViewChild(MatMenuTrigger, {static: true}) matMenuTrigger: MatMenuTrigger;

  private bag: Group;
  public bagPrefix = DRAGULA_BAG_PREFIXES.LEGEND_ITEM;
  public optionDisplayTypes = OPTION_DISPLAY_TYPES;
  public itemTypes = ITEMS_TYPES;

  public blob;

  public bgColor$: Observable<string> = this.sfaHeaderSvc.headerData$.pipe(
    map(header => header.color || this.defaultColor),
    tap(val => (this.currentColor = val))
  );

  private currentColor: string = null;
  private logoExist = false;

  private dragOpts = {
    moves: (el, src, handle, sibling) => {
      return this.modeDataSvc.isEditMode() && this.sfaHeaderSvc.isPinned;
    },
    copy: false
  };

  private $destroyed = new Subject<boolean>();

  constructor(
    public sfaHeaderSvc: SfaHeaderService,
    private sanitizer: DomSanitizer,
    private stylesSvc: StylesService,
    private modeDataSvc: ModeDataService,
    private dragulaService: DragulaService,
    private dragulaConfigurationService: DragulaConfigurationService,
    private q9SnackBar: SnackBarService,
    private diagramTypeConfigSvc: DiagramTypeConfigService
  ) {}

  ngOnInit() {
    this.initDragula();
  }

  ngOnDestroy() {
    this.$destroyed.next(true);
    this.$destroyed.complete();

    if (this.bag && this.resContainerRef) {
      const drake: any = this.bag.drake || {};
      const containers: Element[] = drake.containers || [];
      const index = containers.indexOf(this.resContainerRef.nativeElement);

      if (index > -1) {
        containers.splice(index, 1);
      }
    }
  }

  private initDragula(): void {
    this.bag = this.dragulaConfigurationService.createGroup({
      groupName: this.bagPrefix,
      ...this.dragOpts
    });
    this.dragulaService
      .dropModel(this.bagPrefix)
      .pipe(takeUntil(this.$destroyed))
      .subscribe(data => this.onDrop(data));
  }

  onDrop({target, el}: DragulaEvent<'drop'>): void {
    const cArr = Array.from(target.children || []);

    if (el instanceof HTMLElement) {
      const mxKey = el.dataset.id;
      const newPosition = cArr.indexOf(el);

      this.sfaHeaderSvc.reorderLegendItem(mxKey, newPosition);
    }
  }

  public deleteLegendItem(item): void {
    this.sfaHeaderSvc.deleteLegendItem(item.mxKey);
  }

  public removeLogo(): void {
    this.blob = null;
    this.sfaHeaderSvc.removeLogo();
  }

  public safeImageUrl(url): SafeStyle {
    if (!url) {
      return;
    }

    return this.sanitizer.bypassSecurityTrustStyle(`url(${url})`);
  }

  public onRightClick(event: MouseEvent, item): void {
    if (!this.sfaHeaderSvc.isPinned || !this.modeDataSvc.isEditMode()) {
      return;
    }

    const options = this.getOptions(item.itemType, item.header);

    if (!options) {
      return;
    }

    if (item.header) {
      this.logoExist = !!(item.header.logo || this.blob);
    }

    event.preventDefault();

    this.menuCoords.x = event.clientX + 'px';
    this.menuCoords.y = event.clientY + 'px';

    this.matMenuTrigger.menuData = {item, options};

    this.matMenuTrigger.openMenu();
  }

  private getOptions(type: LegendItemType | ITEMS_TYPES, header): ItemMenuOptions {
    switch (type) {
      case LegendItemType.Card:

      case LegendItemType.Line:
        return this.lineOrCardOptions;

      case LegendItemType.Resource:
        return this.resourceOptions;

      case ITEMS_TYPES.LOGO:
        return this.logoOptions(header);

      case ITEMS_TYPES.INFO:
        return this.infoOptions();

      default:
        return null;
    }
  }

  public openEditItem(item: ILegendItem): void {
    this.sfaHeaderSvc.editLegendItem(item);
  }

  public fileChangeEvent(event: any, fileInput): void {
    const file = event.currentTarget.files[0];
    const MAX_IMAGE_SIZE = 4096; // ~ 4 mb

    if (!file) {
      return;
    }

    if (!/\.(jpe?g|png|gif)$/i.test(file.name)) {
      return this.q9SnackBar.error('ASIDE_R.ERROR_SELECTED_NOT_IMAGE');
    }

    if (!(Math.floor(file.size / 1024) < MAX_IMAGE_SIZE)) {
      return this.q9SnackBar.error('ASIDE_R.ERROR_LARGE_IMG');
    }

    const payload = {
      event,
      isUpload: !this.logoExist,
      callback: () => (fileInput.value = null)
    };

    this.sfaHeaderSvc.changeLogo(payload).subscribe(blob => {
      this.blob = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(blob));
    });
  }

  public changeBackground() {
    this.sfaHeaderSvc.changeColor(this.currentColor);
  }

  private mapLegendItems(header: IHeaderData): IHeaderData {
    if (header.logo) {
      this.blob = null;
    }

    if (header?.legendItems?.length) {
      header.legendItems = header.legendItems.map(item => {
        if (item.itemType === 'line') {
          const mapper = new FlowlineHeaderHelper().getDynamicStyle(item);

          item.dynamicStyle = {
            end: mapper(item.style.endArrow, false),
            start: mapper(item.style.startArrow, true)
          };
        }

        return item;
      });
    }

    return header;
  }

  private get defaultOptions(): ItemMenuOptions {
    return {
      edit: OPTION_DISPLAY_TYPES.HIDE,
      remove: OPTION_DISPLAY_TYPES.HIDE,
      uploadLogo: OPTION_DISPLAY_TYPES.HIDE,
      changeLogo: OPTION_DISPLAY_TYPES.HIDE,
      removeLogo: OPTION_DISPLAY_TYPES.HIDE,
      changeBackground: OPTION_DISPLAY_TYPES.HIDE
    };
  }

  private get lineOrCardOptions(): ItemMenuOptions {
    return {
      ...this.defaultOptions,
      edit: OPTION_DISPLAY_TYPES.SHOW,
      remove: OPTION_DISPLAY_TYPES.SHOW
    };
  }

  private get resourceOptions(): ItemMenuOptions {
    return {
      ...this.defaultOptions,
      edit: OPTION_DISPLAY_TYPES.DISABLE,
      remove: OPTION_DISPLAY_TYPES.SHOW
    };
  }

  private infoOptions(): ItemMenuOptions {
    return this.sfaHeaderSvc.colorModalOpen()
      ? null
      : {...this.defaultOptions, changeBackground: OPTION_DISPLAY_TYPES.SHOW};
  }

  private logoOptions(header): ItemMenuOptions {
    const hasLogo = !!header.logo || !!this.blob;

    return {
      ...this.defaultOptions,
      uploadLogo: hasLogo ? OPTION_DISPLAY_TYPES.DISABLE : OPTION_DISPLAY_TYPES.SHOW,
      changeLogo: hasLogo ? OPTION_DISPLAY_TYPES.SHOW : OPTION_DISPLAY_TYPES.DISABLE,
      removeLogo: hasLogo ? OPTION_DISPLAY_TYPES.SHOW : OPTION_DISPLAY_TYPES.DISABLE
    };
  }
}
